import React, { useEffect, useRef, useState } from 'react'
import InputGroup from '../inputComp/InputGroup'
import { AddressIcon, DepartmentIcon, EmailIcon, FaxIcon, MobileIcon, NameIcon } from '../iconSet/IconSet'
import { CreateDepartmentPropsTypes, TabTypes } from './CreateDepartmentType'
import { InputGroupPropsTypes } from '../inputComp/InputGroupType'
import { API_ADD_NEW_DEPARTMENT_URL, APIAddNewDepartmentTypes } from '../../services/APIDepartmentService'
import { ACCESS_TOKEN_INVALID, APICall, DATA_VALIDATION_ERROR, INTERNAL_SERVER_ERROR, RECODE_ALREADY_EXIST, ROLE_HAVE_NOT_PERMISSION, SEQUENCE_GENERATION_ERROR } from '../../services/APIService'
import { useAlert } from '../../context/AlertContext'
import { getRefreshToken } from '../../utils/APIUtils'
import LoadingScreen from '../loadingScreenComp/LoadingScreen'

const CreateDepartment = (props: CreateDepartmentPropsTypes) => {

    const tabs: string[] = ['create_user', 'policies']
    const [selectedTab, setSelectedTab] = useState<TabTypes>('create_department')

    // Loading screen
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const {showAlert} = useAlert()

    const organizationNameRef = useRef<HTMLInputElement | null>(null)
    const shortNameRef = useRef<HTMLInputElement | null>(null)
    const emailAddressRef = useRef<HTMLInputElement | null>(null)
    const faxNumberRef = useRef<HTMLInputElement | null>(null)
    const departmentNameRef = useRef<HTMLInputElement | null>(null)
    const addressRef = useRef<HTMLInputElement | null>(null)
    const telephoneNumberRef = useRef<HTMLInputElement | null>(null)

    const formItems: InputGroupPropsTypes[] = [
        {
            type: 'text',
            icon: <NameIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter organization name',
            label: 'organization name',
            name: 'organizationName',
            componentInputElement: organizationNameRef,
        },
        {
            type: 'text',
            icon: <NameIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter short name',
            label: 'short name',
            name: 'shortName',
            componentInputElement: shortNameRef,
        },
        {
            type: 'text',
            icon: <EmailIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter email address',
            label: 'email address',
            name: 'email',
            componentInputElement: emailAddressRef,
        },
        {
            type: 'text',
            icon: <FaxIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter fax number',
            label: 'fax number',
            name: 'fax',
            componentInputElement: faxNumberRef,
        },
        {
            type: 'text',
            icon: <DepartmentIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter department name',
            label: 'department name',
            name: 'department',
            componentInputElement: departmentNameRef,
        },
        {
            type: 'text',
            icon: <AddressIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter address',
            label: 'address',
            name: 'address',
            componentInputElement: addressRef,
        },
        {
            type: 'text',
            icon: <MobileIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Enter telephone number',
            label: 'telephone number',
            name: 'telephone',
            componentInputElement: telephoneNumberRef,
        }
    ]

    /* Save changes button click */
    const handleSaveChangeClick = async () => {
        setIsLoading(true)
        // Get new access token
        getRefreshToken()

        const accessToken: string | null = window.localStorage.getItem("accessToken")
        
        if(accessToken)
        {
            if(shortNameRef.current && emailAddressRef.current && faxNumberRef.current && departmentNameRef.current && addressRef.current && telephoneNumberRef.current && organizationNameRef.current)
            {
                const APINewDepartmentBody: APIAddNewDepartmentTypes = {
                    accessToken: accessToken,
                    shortName: shortNameRef.current.value,
                    email: emailAddressRef.current.value,
                    faxNumber: faxNumberRef.current.value,
                    departmentName: departmentNameRef.current?.value,
                    address: addressRef.current.value,
                    telephoneNumber: telephoneNumberRef.current.value,
                    organizationName: organizationNameRef.current.value
                }

                const response = await APICall(API_ADD_NEW_DEPARTMENT_URL, 'POST', APINewDepartmentBody)

                if(response.errorCode === SEQUENCE_GENERATION_ERROR)
                {
                    console.log("sequence error")
                    showAlert('sequence error', 'ERROR')
                }
                else if(response.errorCode === INTERNAL_SERVER_ERROR){
                    console.log("internal server error")
                    showAlert('internal server error', 'ERROR')
                }
                else if(response.errorCode === RECODE_ALREADY_EXIST){
                    console.log("department already exists")
                    showAlert('department already exists', 'ERROR')
                }
                else if(response.errorCode === ROLE_HAVE_NOT_PERMISSION){
                    console.log("role permission denied")
                    showAlert('you don\'t have access to to this', 'ERROR')
                }
                else if(response.errorCode === ACCESS_TOKEN_INVALID){
                    console.log("access token invalid")
                    showAlert('access token invalid', 'ERROR')
                }
                else if(response.errorCode === DATA_VALIDATION_ERROR){
                    console.log("data invalid: ", response.data)
                    showAlert('data validation failed', 'ERROR')
                }
                else if(response.httpStatus === 'OK'){
                    shortNameRef.current.value = ''
                    emailAddressRef.current.value = ''
                    faxNumberRef.current.value = ''
                    departmentNameRef.current.value = ''
                    addressRef.current.value = ''
                    telephoneNumberRef.current.value = ''
                    organizationNameRef.current.value = ''

                    showAlert('Department added successfully', 'SUCCESS')
                }
            }
        }
        setIsLoading(false)
    }

    // Cancel button click
    const handleCancelClick = () => {
        if(shortNameRef.current && emailAddressRef.current && faxNumberRef.current && departmentNameRef.current && addressRef.current && telephoneNumberRef.current && organizationNameRef.current)
        {
            shortNameRef.current.value = ''
            emailAddressRef.current.value = ''
            faxNumberRef.current.value = ''
            departmentNameRef.current.value = ''
            addressRef.current.value = ''
            telephoneNumberRef.current.value = ''
            organizationNameRef.current.value = ''
        }
    }
    
    return (
        <div className={`container-wrapper`}>
            <span className={`title`}>create department</span>
            
            <div className={`create-department`}>
                {/* Early warning -> workflow diagram -> create department */}
                <div className={`grid-layout-create-department`}>
                    <div className={`left-side`}>
                        {formItems.slice(0, 4).map((field, index) => (
                            <InputGroup key={index} {...field}/>
                        ))}
                    </div>

                    <div className={`right-side`}>
                        {formItems.slice(4).map((field, index) => (
                            <InputGroup key={index} {...field}/>
                        ))}
                    </div>

                    <div className={`button-wrapper`} style={{gridArea: 'button'}}>
                        <div className={`left-side`}></div>
                        <div className={`right-side`}>
                            <button className={`button-normal`} onClick={(e) => handleSaveChangeClick()}>save changes</button>
                            <button className={`button-normal`} onClick={(e) => handleCancelClick()}>cancel</button>
                        </div>
                    </div>
                </div>
            </div>
            {isLoading ? <LoadingScreen /> : null}
        </div>
    )
}

export default CreateDepartment